import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  FileInput,
  Textarea,
  Alert,
  Loader,
  Code,
  Divider,
  TextInput,
  Select,
  NumberInput,
  Progress,
  Table
} from '@mantine/core';
import {
  IconArrowLeft,
  IconUpload,
  IconAlertCircle,
  IconDownload,
  IconFileSpreadsheet
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { importExcel, judgeImport } from '../api_scripts/importsClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';
import { useDids } from '../hooks/useDids';
import { downloadExcelTemplate } from '../utils/excelTemplate';
import SynthesizedPlanList from './SynthesizedPlanList';
import JudgeProgressModal from '../../../components/JudgeProgressModal';

// fromDid is now sourced from /dids via useDids() — the hook supplies the
// env's primary DID at mount, with engineer-overridable Select. Hardcoded
// fallback kept only as a last-resort placeholder.
const FALLBACK_FROM_DID_PLACEHOLDER = '+10000000000';
// Lower default than the backend's 50-plan cap. Smaller batches give the model
// a better shot at returning complete JSON — large spreadsheets can re-import
// in multiple passes via the maxPlans NumberInput.
const DEFAULT_MAX_PLANS = 15;
const ACCEPTED_TYPES = '.xlsx';

const CCAAS_OPTIONS = [
  { value: 'wxcc', label: 'WebEx Contact Center' },
  { value: 'aws-connect', label: 'AWS Connect' },
  { value: 'five9', label: 'Five9' },
  { value: 'genesys', label: 'Genesys' }
];

// Presets for the default expectPrompt timeoutSec the importer sends to
// Bedrock. The backend clamps anything outside [5, 60].
const IVR_PACE_PRESETS = { fast: 5, standard: 8, slow: 15 };
const IVR_PACE_OPTIONS = [
  { value: 'fast', label: 'Fast — touch-tone menus (5s)' },
  { value: 'standard', label: 'Standard — modern IVRs (8s)' },
  { value: 'slow', label: 'Slow — government / healthcare / long preambles (15s)' },
  { value: 'custom', label: 'Custom — set my own…' }
];

function resolveBaseTimeout(pace, custom) {
  if (pace === 'custom') {
    const n = Number(custom);
    if (!Number.isFinite(n)) return IVR_PACE_PRESETS.fast;
    // Chime SDK SMA Pause action rejects > 30000ms; align the frontend bound.
    return Math.min(30, Math.max(5, Math.round(n)));
  }
  return IVR_PACE_PRESETS[pace] ?? IVR_PACE_PRESETS.fast;
}

// Header column synonyms. Match by squashing to lowercase-letters-only.
const COL_SYNONYMS = {
  testNo: ['testno', 'testnum', 'testnumber', 'testcase'],
  expectedResults: ['expectedresults', 'expectedresult', 'description', 'scenario'],
  preconditions: ['preconditions', 'precondition', 'setup'],
  sequence: ['sequence', 'step', 'seq'],
  callerExperience: ['callerexperience', 'caller', 'callerexp'],
  agentExperience: ['agentexperience', 'agent', 'agentexp']
};

function cellText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v?.richText)) return v.richText.map((t) => t.text).join('');
  if (typeof v?.text === 'string') return v.text;
  if (v?.result !== undefined) return String(v.result);
  if (v?.formula) return '';
  if (v?.hyperlink) return v.text || v.hyperlink;
  return String(v);
}

function squashHeader(s) {
  return String(s || '').toLowerCase().replace(/[^a-z]/g, '');
}

function matchColumn(headerText) {
  const sq = squashHeader(headerText);
  for (const [key, syns] of Object.entries(COL_SYNONYMS)) {
    if (syns.some((s) => sq === s || sq.startsWith(s))) return key;
  }
  return null;
}

/**
 * Parse the first sheet of an .xlsx file into deterministic group structure.
 *
 * Layout assumed (matches the standard vendor QA test-script template):
 *   - 0..N banner rows (Queue Name / Production TFN / AWS DIDs) above header
 *   - Header row containing "Test No#" + "Sequence" + "Caller Experience"
 *   - Section banner rows (one merged value across all columns) below header
 *   - Per-step rows grouped by Test No# (merged in column A)
 *   - Blank separator rows between tests
 */
async function parseWorkbook(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const ws = wb.worksheets[0];
  if (!ws) throw new Error('Workbook has no sheets.');

  const lastCol = Math.max(ws.columnCount || 0, 8);

  let headerRowIdx = -1;
  const SCAN_LIMIT = Math.min(ws.rowCount, 20);
  for (let r = 1; r <= SCAN_LIMIT; r++) {
    const cells = [];
    for (let c = 1; c <= lastCol; c++) {
      cells.push(squashHeader(cellText(ws.getRow(r).getCell(c).value)));
    }
    const joined = cells.join('|');
    if (joined.includes('testno') && joined.includes('sequence') && joined.includes('callerexperience')) {
      headerRowIdx = r;
      break;
    }
  }
  if (headerRowIdx === -1) {
    throw new Error(
      'Could not find a header row. Expected columns: Test No# | Expected Results | Preconditions | Sequence | Caller Experience | Agent Experience.'
    );
  }

  const colMap = {};
  const headerRow = ws.getRow(headerRowIdx);
  for (let c = 1; c <= lastCol; c++) {
    const key = matchColumn(cellText(headerRow.getCell(c).value));
    if (key && !colMap[key]) colMap[key] = c;
  }
  const missing = ['testNo', 'sequence', 'callerExperience'].filter((k) => !colMap[k]);
  if (missing.length > 0) {
    throw new Error(`Header row is missing required columns: ${missing.join(', ')}.`);
  }

  const banner = { queueName: '', productionTfn: '', awsDids: '' };
  const findValueAfterLabel = (rowIdx, labelMatcher) => {
    const row = ws.getRow(rowIdx);
    let labelCol = -1;
    for (let c = 1; c <= lastCol; c++) {
      const txt = String(cellText(row.getCell(c).value) || '').toLowerCase();
      if (labelMatcher.test(txt)) { labelCol = c; break; }
    }
    if (labelCol === -1) return '';
    const labelTxt = String(cellText(row.getCell(labelCol).value) || '').trim();
    for (let c = labelCol + 1; c <= lastCol; c++) {
      const v = String(cellText(row.getCell(c).value) || '').trim();
      if (v && v !== labelTxt) return v;
    }
    return '';
  };
  for (let r = 1; r < headerRowIdx; r++) {
    if (!banner.queueName) banner.queueName = findValueAfterLabel(r, /queue\s*name/);
    if (!banner.productionTfn) {
      banner.productionTfn =
        findValueAfterLabel(r, /production\s*tfn/) || findValueAfterLabel(r, /\btest\s*tfn/);
    }
    if (!banner.awsDids) banner.awsDids = findValueAfterLabel(r, /(aws\s*did|inbound\s*did)/);
  }

  const groupsMap = new Map();
  let currentSection = null;
  const warnings = [];
  for (let r = headerRowIdx + 1; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const cellValues = [];
    for (let c = 1; c <= lastCol; c++) {
      cellValues.push(String(cellText(row.getCell(c).value) || '').trim());
    }
    const nonEmpty = cellValues.filter((v) => v.length > 0);
    if (nonEmpty.length === 0) continue;

    const distinct = new Set(nonEmpty);
    if (distinct.size === 1 && nonEmpty.length >= 4) {
      currentSection = nonEmpty[0];
      continue;
    }

    const testNo = cellValues[colMap.testNo - 1];
    const sequence = cellValues[colMap.sequence - 1];
    const callerExperience = cellValues[colMap.callerExperience - 1];
    const agentExperience = colMap.agentExperience ? cellValues[colMap.agentExperience - 1] : '';
    const expectedResults = colMap.expectedResults ? cellValues[colMap.expectedResults - 1] : '';
    const preconditions = colMap.preconditions ? cellValues[colMap.preconditions - 1] : '';

    if (!testNo || !callerExperience) continue;

    if (!groupsMap.has(testNo)) {
      groupsMap.set(testNo, {
        testNo,
        section: currentSection || null,
        expectedResults,
        preconditions,
        rows: []
      });
    }
    groupsMap.get(testNo).rows.push({
      sequence: sequence || String(groupsMap.get(testNo).rows.length + 1),
      callerExperience,
      ...(agentExperience ? { agentExperience } : {})
    });
  }

  const groups = Array.from(groupsMap.values());
  if (groups.length === 0) {
    warnings.push('No test cases were detected below the header row.');
  }
  return { sheetName: ws.name, banner, groups, warnings };
}

function ExcelImporter({ onBack }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState(null);

  const { dids, primary: primaryDid, loading: didsLoading } = useDids();
  const [ccaas, setCcaas] = useState('wxcc');
  const [destination, setDestination] = useState('');
  const [fromDid, setFromDid] = useState('');
  useEffect(() => {
    if (!fromDid && primaryDid) setFromDid(primaryDid);
  }, [primaryDid, fromDid]);
  const [notes, setNotes] = useState('');
  const [maxPlans, setMaxPlans] = useState(DEFAULT_MAX_PLANS);
  // matcherPreference drives how the Bedrock importer chooses between exact
  // substring matchers and semantic intent matchers for each expectPrompt step.
  //   auto    — substring when row text contains a quoted phrase (≥4 words),
  //             semantic otherwise. Current default.
  //   exact   — substring whenever the row text contains any "..." quoted
  //             phrase, regardless of length. Semantic only when no quotes.
  //             Best for QA spreadsheets that consistently quote verbatim IVR
  //             prompts (DHS-style).
  //   semantic— always semantic intent matchers; substring is never emitted.
  //             Best for voice-AI destinations whose phrasing varies turn-to-turn.
  const [matcherPreference, setMatcherPreference] = useState('auto');
  // IVR pace preset drives the default expectPrompt timeoutSec the importer
  // sends to Bedrock. Custom unlocks a NumberInput so the user can dial in
  // the duration of their longest expected prompt.
  const [ivrPace, setIvrPace] = useState('fast');
  const [customTimeoutSec, setCustomTimeoutSec] = useState(15);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [chunkProgress, setChunkProgress] = useState(null);

  // Quality-judge advisory state. judgeRunIdRef guards against stale
  // responses if the user regenerates while a previous judge is in flight.
  const [judgeEvaluating, setJudgeEvaluating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityFindings, setQualityFindings] = useState([]);
  const judgeRunIdRef = useRef(0);

  const backendConfigured = isSitBackendConfigured();
  const navigate = useNavigate();
  const resultsRef = useRef(null);

  useEffect(() => {
    // After a successful synthesize, scroll the results card into view. The
    // result list renders below the form + parsed preview; on long sheets
    // (e.g. 29-test spreadsheets) the user lands well below the fold otherwise.
    if (result?.plans?.length > 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  const handleFileChange = useCallback(async (next) => {
    setFile(next);
    setParsed(null);
    setParseError(null);
    setResult(null);
    setError(null);
    if (!next) return;
    try {
      const out = await parseWorkbook(next);
      setParsed(out);
      if (out.banner.productionTfn && !destination) setDestination(out.banner.productionTfn);
    } catch (e) {
      setParseError(e.message || 'Failed to parse spreadsheet.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (parsed?.banner?.productionTfn && !destination) {
      setDestination(parsed.banner.productionTfn);
    }
  }, [parsed, destination]);

  // Build a text snapshot of the parsed Excel content the judge can grade
  // plans against (faithfulness, completeness, matcher-type fit).
  const buildExcelSourceContent = (groups, parsedSheet) => {
    const lines = [];
    if (parsedSheet?.banner) {
      lines.push(`BANNER: ${JSON.stringify(parsedSheet.banner)}`);
    }
    groups.forEach((g) => {
      lines.push(`\n=== Test #${g.testNo}: ${g.expectedResults || '(no description)'} ===`);
      if (g.preconditions) lines.push(`Preconditions: ${g.preconditions}`);
      g.rows.forEach((r) => {
        lines.push(`  [${r.sequence}] caller: ${r.callerExperience}`);
        if (r.agentExperience) lines.push(`        agent: ${r.agentExperience}`);
      });
    });
    return lines.join('\n');
  };

  const startJudge = async ({ plans, groups, parsedSheet }) => {
    const runId = ++judgeRunIdRef.current;
    setJudgeEvaluating(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const sourceContent = buildExcelSourceContent(groups, parsedSheet);
      const data = await judgeImport({
        kind: 'sit-plan',
        source: {
          kind: 'excel',
          description: `${groups.length} test case(s) from ${parsedSheet?.sheetName || 'spreadsheet'}`
        },
        sourceContent,
        plans
      });
      if (judgeRunIdRef.current !== runId) return;
      if (typeof data?.overallScore === 'number') setQualityScore(data.overallScore);
      if (Array.isArray(data?.perItem)) setQualityFindings(data.perItem);
    } catch (e) {
      if (judgeRunIdRef.current !== runId) return;
      // eslint-disable-next-line no-console
      console.warn('quality-judge call failed:', e);
    } finally {
      if (judgeRunIdRef.current === runId) setJudgeEvaluating(false);
    }
  };

  const runImport = async (priorJudgeFindings) => {
    if (!parsed || parsed.groups.length === 0) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    setChunkProgress(null);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const payload = {
        groups: parsed.groups,
        targetDefaults: {
          ccaas,
          ...(destination ? { destination } : {}),
          ...(fromDid ? { fromDid } : {})
        },
        source: {
          filename: file?.name || null,
          sheetName: parsed.sheetName
        },
        notes: notes.trim() || undefined,
        maxPlans,
        matcherPreference,
        expectPromptBaseTimeoutSec: resolveBaseTimeout(ivrPace, customTimeoutSec),
        ...(priorJudgeFindings && priorJudgeFindings.length > 0
          ? { priorJudgeFindings }
          : {})
      };
      const data = await importExcel(payload, { onChunkProgress: setChunkProgress });
      setResult(data);
      if (data.plans?.length > 0) {
        startJudge({ plans: data.plans, groups: parsed.groups, parsedSheet: parsed });
      }
    } catch (e) {
      const body = e?.response?.data;
      // Prefer `context.detail` so the model's actual prose surfaces — same
      // rationale as DiagramImporter.js handleSubmit catch.
      setError(
        body?.context?.detail ||
          body?.message ||
          body?.code ||
          e.message ||
          'Import failed.'
      );
    } finally {
      setSubmitting(false);
      setChunkProgress(null);
    }
  };

  const handleSubmit = () => runImport();
  const handleRegenerate = (findings) => runImport(findings);

  const totalRows = useMemo(
    () => (parsed?.groups || []).reduce((acc, g) => acc + g.rows.length, 0),
    [parsed]
  );

  return (
    <Container size="lg" px="md">
      <Group justify="space-between" mb="lg">
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back
          </Button>
          <Title order={2}>Excel Test-Script Importer</Title>
          <Badge color="violet" variant="light">SIT</Badge>
        </Group>
        <Button
          variant="light"
          color="violet"
          leftSection={<IconDownload size={16} />}
          onClick={() => downloadExcelTemplate()}
        >
          Download blank template
        </Button>
      </Group>

      <Text c="dimmed" mb="md">
        Drop an .xlsx test-script (vendor QA layout — rows grouped by Test No#, one step per
        row). Each Test No# becomes one SIT v1 plan; the free-form <em>Caller Experience</em> column
        is synthesized into typed steps by Bedrock on the backend.
      </Text>

      {!backendConfigured && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" mb="md">
          <Text size="sm">
            SIT backend is not configured (<Code>REACT_APP_SIT_API_BASE</Code> is unset). You can
            still parse and preview the spreadsheet locally; the Import button is disabled.
          </Text>
        </Alert>
      )}

      <Card withBorder shadow="sm" radius="md" p="lg" mb="lg">
        <Stack gap="md">
          <FileInput
            label="Test-script spreadsheet"
            description=".xlsx — single sheet, header row with Test No#, Sequence, Caller Experience"
            placeholder="Choose .xlsx file"
            accept={ACCEPTED_TYPES}
            value={file}
            onChange={handleFileChange}
            leftSection={<IconFileSpreadsheet size={16} />}
            clearable
          />

          {parseError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>{parseError}</Alert>
          )}

          {parsed && (
            <>
              <Group gap="xs">
                <Badge variant="light" color="violet">{parsed.groups.length} test cases</Badge>
                <Badge variant="light">{totalRows} step rows</Badge>
                <Badge variant="light" color="gray">sheet: {parsed.sheetName}</Badge>
                {parsed.banner.queueName && (
                  <Badge variant="light" color="cyan">queue: {parsed.banner.queueName}</Badge>
                )}
              </Group>
              {parsed.warnings?.length > 0 && (
                <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
                  {parsed.warnings.join(' ')}
                </Alert>
              )}
            </>
          )}

          <Divider label="Target defaults (applied to every synthesized plan)" labelPosition="left" />

          <Group grow>
            <Select
              label="CCaaS platform"
              data={CCAAS_OPTIONS}
              value={ccaas}
              onChange={(v) => v && setCcaas(v)}
            />
            <TextInput
              label="Destination DID"
              description="E.164, e.g. +15551234567"
              placeholder="+1..."
              value={destination}
              onChange={(e) => setDestination(e.currentTarget.value)}
            />
            <Select
              label="From DID (harness)"
              placeholder={didsLoading ? 'Loading…' : (dids.length === 0 ? 'No DIDs configured in this env' : 'Pick a Chime DID')}
              data={dids.map((d) => ({ value: d.did, label: d.primary ? `${d.did} (primary)` : d.did }))}
              value={fromDid || (didsLoading ? '' : FALLBACK_FROM_DID_PLACEHOLDER)}
              onChange={(v) => setFromDid(v || '')}
              searchable
              disabled={didsLoading || dids.length === 0}
            />
          </Group>

          <Group grow align="flex-start">
            <Textarea
              label="Notes for Bedrock (optional)"
              description="Extra context to bias step synthesis (e.g. holiday-greeting wording, voicemail prompt phrasing)."
              minRows={2}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
            <Stack gap="sm" style={{ maxWidth: 280 }}>
              <NumberInput
                label="Max plans to synthesize"
                description="Soft cap on the number of plans the backend returns."
                value={maxPlans}
                onChange={setMaxPlans}
                min={1}
                max={100}
              />
              <Select
                label="Prompt matcher preference"
                description='How "Caller hears" rows become matchers'
                value={matcherPreference}
                onChange={(v) => setMatcherPreference(v || 'auto')}
                data={[
                  { value: 'auto', label: 'Auto — substring when QA quoted (≥4 words), else semantic' },
                  { value: 'exact', label: 'Prefer exact — substring whenever QA quoted any phrase' },
                  { value: 'semantic', label: 'Always semantic — voice-AI / variable wording' }
                ]}
                allowDeselect={false}
              />
              <Select
                label="IVR pace"
                description="Default timeoutSec the importer uses on every expectPrompt step"
                value={ivrPace}
                onChange={(v) => setIvrPace(v || 'fast')}
                data={IVR_PACE_OPTIONS}
                allowDeselect={false}
              />
              {ivrPace === 'custom' && (
                <NumberInput
                  label="Custom default timeout (sec)"
                  description="Set this to the duration of your LONGEST expected IVR prompt (max 30s — Chime SDK Pause limit). The importer uses it as the default for every expectPrompt step — you can still override individual steps on the Verify Plans screen."
                  value={customTimeoutSec}
                  onChange={(v) => setCustomTimeoutSec(typeof v === 'number' ? v : 15)}
                  min={5}
                  max={30}
                />
              )}
            </Stack>
          </Group>

          <Group justify="flex-end">
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={handleSubmit}
              loading={submitting}
              disabled={!parsed || parsed.groups.length === 0 || !backendConfigured}
            >
              Import &amp; synthesize plans
            </Button>
          </Group>

          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>
          )}
        </Stack>
      </Card>

      {parsed && parsed.groups.length > 0 && (
        <Card withBorder shadow="sm" radius="md" p="lg" mb="lg">
          <Title order={4} mb="sm">Parsed preview</Title>
          <Text c="dimmed" size="sm" mb="md">
            One card per detected test case. The backend will synthesize each card into one SIT v1
            plan.
          </Text>
          <Stack gap="md">
            {parsed.groups.map((g) => (
              <Card key={g.testNo} withBorder padding="sm" radius="sm">
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <Badge color="violet">Test #{g.testNo}</Badge>
                    {g.section && <Badge variant="light" color="gray">{g.section}</Badge>}
                  </Group>
                  <Badge variant="light">{g.rows.length} steps</Badge>
                </Group>
                {g.expectedResults && (
                  <Text size="sm" fw={500} mb={4}>{g.expectedResults}</Text>
                )}
                {g.preconditions && (
                  <Text size="xs" c="dimmed" mb="xs" style={{ whiteSpace: 'pre-wrap' }}>
                    Preconditions: {g.preconditions}
                  </Text>
                )}
                <Table withTableBorder withColumnBorders fontSize="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 60 }}>Seq</Table.Th>
                      <Table.Th>Caller Experience</Table.Th>
                      {g.rows.some((r) => r.agentExperience) && (
                        <Table.Th>Agent Experience</Table.Th>
                      )}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {g.rows.map((row, i) => (
                      <Table.Tr key={i}>
                        <Table.Td>{row.sequence}</Table.Td>
                        <Table.Td style={{ whiteSpace: 'pre-wrap' }}>{row.callerExperience}</Table.Td>
                        {g.rows.some((r) => r.agentExperience) && (
                          <Table.Td style={{ whiteSpace: 'pre-wrap' }}>{row.agentExperience || ''}</Table.Td>
                        )}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            ))}
          </Stack>
        </Card>
      )}

      {submitting && (
        <Card withBorder padding="md" radius="md" mb="lg">
          <Group gap="sm" mb={chunkProgress?.totalChunks > 1 ? 'xs' : 0}>
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {chunkProgress?.totalChunks > 1
                ? `Synthesizing plans on Bedrock — batch ${chunkProgress.chunk}/${chunkProgress.totalChunks} (${chunkProgress.plansSoFar}/${chunkProgress.totalPlans} plans so far)…`
                : 'Synthesizing plans on Bedrock…'}
            </Text>
          </Group>
          {chunkProgress?.totalChunks > 1 && (
            <Progress
              value={(chunkProgress.plansSoFar / chunkProgress.totalPlans) * 100}
              animated
            />
          )}
        </Card>
      )}

      {result?.plans?.length > 0 && (
        <div ref={resultsRef} style={{ scrollMarginTop: 16 }}>
          <SynthesizedPlanList
            plans={result.plans}
            modelId={result.modelId}
            retries={result.retries}
            warnings={result.warnings || []}
            sourceLabel="Excel import"
            onVerify={(plans) =>
              navigate('/verify', { state: { plans, source: 'Excel import' } })
            }
            evaluating={judgeEvaluating}
            qualityScore={qualityScore}
            qualityFindings={qualityFindings}
            onRegenerate={handleRegenerate}
          />
        </div>
      )}

      <JudgeProgressModal
        opened={judgeEvaluating}
        onClose={() => { /* parent controls via evaluating flag */ }}
        kind="sit-plan"
        color="violet"
      />
    </Container>
  );
}

export default ExcelImporter;
